import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, VEHICLES } from "../lib/api";
import DateTimePicker from "./DateTimePicker";

const initialState = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  pickup_location: "",
  destination: "",
  pickup_datetime: null,
  vehicle: VEHICLES[0].name,
  passengers: 1,
  notes: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.pickup_location || !form.destination || !form.pickup_datetime) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        pickup_datetime: form.pickup_datetime.toISOString(),
        passengers: Number(form.passengers),
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmation(data);
      toast.success("Booking received! We'll confirm shortly.");
      setForm(initialState);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit booking. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Failed to submit booking");
    } finally {
      setLoading(false);
    }
  };

  if (confirmation) {
    return (
      <section id="book" className="relative bg-black py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#0A0A0A] border border-[#FFC300] p-10 lg:p-14"
            data-testid="booking-confirmation"
          >
            <CheckCircle2 className="w-12 h-12 text-[#FFC300] mb-6" />
            <h3 className="font-display text-3xl lg:text-5xl uppercase font-black tracking-tighter text-white">
              Booking Received
            </h3>
            <p className="mt-4 text-neutral-300 text-lg">
              Thank you, <span className="text-white font-semibold">{confirmation.customer_name}</span>.
              Your booking has been sent to our dispatch.
            </p>
            <p className="mt-2 text-[#FFC300] font-semibold">
              Driver will contact you shortly.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {[
                ["Booking ID", confirmation.id.slice(0, 8).toUpperCase()],
                ["Status", confirmation.status],
                ["Pickup", confirmation.pickup_location],
                ["Destination", confirmation.destination],
                ["Vehicle", confirmation.vehicle],
                ["Passengers", String(confirmation.passengers)],
                ["Date & Time", new Date(confirmation.pickup_datetime).toLocaleString()],
              ].map(([k, v]) => (
                <div key={k} className="bg-black px-5 py-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{k}</div>
                  <div className="text-white font-semibold mt-1 capitalize">{v}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setConfirmation(null)}
              data-testid="new-booking-btn"
              className="mt-8 inline-flex bg-[#FFC300] text-black font-bold uppercase tracking-widest text-xs px-7 py-4 hover:bg-white transition-colors"
            >
              Book Another Ride
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="book" className="relative bg-black py-16 sm:py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
        <div className="lg:col-span-4">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#FFC300] font-bold">
            01 · Reserve
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black uppercase tracking-tighter leading-none">
            Book Your<br />
            <span className="text-[#FFC300]">Ride</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-neutral-400 leading-relaxed text-sm sm:text-base">
            Tell us where, when, and which vehicle. We'll dispatch the closest driver — 24 hours a day.
          </p>
        </div>

        <form onSubmit={submit} data-testid="booking-form" className="lg:col-span-8 bg-[#0A0A0A] border border-[#262626] p-6 sm:p-8 lg:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Full Name" required>
              <input
                data-testid="input-name"
                type="text"
                value={form.customer_name}
                onChange={(e) => update("customer_name", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="Your full name"
                required
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="input-email"
                type="email"
                value={form.customer_email}
                onChange={(e) => update("customer_email", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="you@email.com"
                required
              />
            </Field>
            <Field label="Phone" required>
              <input
                data-testid="input-phone"
                type="tel"
                value={form.customer_phone}
                onChange={(e) => update("customer_phone", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="+34 ..."
                required
              />
            </Field>
            <Field label="Date & Time" icon={Calendar} required>
              <DateTimePicker
                value={form.pickup_datetime}
                onChange={(d) => update("pickup_datetime", d)}
                testId="input-datetime"
              />
            </Field>
            <Field label="Pickup Location" icon={MapPin} required className="md:col-span-2">
              <input
                data-testid="input-pickup"
                type="text"
                value={form.pickup_location}
                onChange={(e) => update("pickup_location", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="e.g., Plaza Catalunya, Barcelona"
                required
              />
            </Field>
            <Field label="Destination" icon={MapPin} required className="md:col-span-2">
              <input
                data-testid="input-destination"
                type="text"
                value={form.destination}
                onChange={(e) => update("destination", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                placeholder="e.g., Barcelona Airport (BCN)"
                required
              />
            </Field>
            <Field label="Vehicle">
              <select
                data-testid="select-vehicle"
                value={form.vehicle}
                onChange={(e) => update("vehicle", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full appearance-none cursor-pointer transition-colors"
              >
                {VEHICLES.map((v) => (
                  <option key={v.id} value={v.name} className="bg-black text-white">
                    {v.name} · up to {v.capacity}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Passengers" icon={Users}>
              <input
                data-testid="input-passengers"
                type="number"
                min={1}
                max={10}
                value={form.passengers}
                onChange={(e) => update("passengers", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors"
                required
              />
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <textarea
                data-testid="input-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="bg-transparent border-b border-[#262626] focus:border-[#FFC300] py-3 text-white outline-none w-full transition-colors resize-none"
                placeholder="Flight number, child seat, luggage…"
              />
            </Field>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-end gap-6 border-t border-white/10 pt-6">
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-booking-btn"
              className="inline-flex items-center gap-3 bg-[#FFC300] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white disabled:opacity-60 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Sending…" : "Confirm Booking"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

const Field = ({ label, icon: Icon, children, required, className = "" }) => (
  <label className={`block ${className}`}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#FFC300]" />}
      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400">
        {label} {required && <span className="text-[#FFC300]">*</span>}
      </span>
    </div>
    {children}
  </label>
);
